import { ITransactionsDataDetail, ITransactionsData } from 'src/interfaces/transactions';
import Text from 'src/components/Text';
import dayjs from 'dayjs';
import { PRIMARY_BLACK, SECONDARY_GRAY_80 } from 'src/global/theme/colors';
import Divider from '@mui/material/Divider';
import { formatMoney } from 'src/utils/global';
import StatusLabel from 'src/components/StatusLabel';
import {
  getImageIssuer,
  getImageMerchant,
  getNameCardType,
  getStatusTransactionLabel
} from "src/components/TransactionsComponent/utils";
import warningIcon from 'src/global/assets/icons/warning.svg';
import Icon from 'src/components/Icon';
import { StatusTransactionLabelColor } from 'src/constants/status';
import { IconSize } from 'src/components/Icon/constants';

import TransactionLogDetail from '../TransactionLogDetail';
import TransactionOperationError from '../TransactionOperationError';

import { styles } from './styles';

interface Props {
  title?: string;
  component?: React.ReactNode;
}
export const makeArrayTransactionData = (
  transaction: ITransactionsData,
  transactionDetail: ITransactionsDataDetail,
  hasP2mInfoPermission: boolean
): Props[] => {
  const { classes } = styles();

  const storeNameConditional = transaction?.merchant?.name
    ? {
        title: 'Comercio',
        component: (
          <div className={classes.titleDrawer}>
            <div className={classes.iconContainer}>
              <Icon
                source={getImageMerchant(transaction?.merchant?.image_url)}
                size={IconSize.MEDIUM}
                className={classes.commerceIcon}
              />
            </div>
            <div className={classes.textContainer}>
              <Text variant="Caption-Regular" color={PRIMARY_BLACK}>
                {transaction?.merchant?.name}
              </Text>

              <Text variant="Callout-Regular" color={SECONDARY_GRAY_80}>
                {transaction?.merchant?.address ? transaction?.merchant?.address : ''}
              </Text>
            </div>
          </div>
        )
      }
    : {};

  return [
    storeNameConditional,
    {
      title: 'Terminal',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {transactionDetail?.terminal_id}
        </Text>
      )
    },
    {
      title: 'Fecha',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {dayjs(transactionDetail?.payment_date).format('DD/MM/YYYY')}
        </Text>
      )
    },
    {
      title: 'Hora',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {dayjs(transactionDetail?.payment_date).format('HH:mm')}
        </Text>
      )
    },
    {
      title: '',
      component: <Divider className={classes.divider} />
    },
    {
      title: 'Monto Bruto',
      component: (
        <Text variant="H4-Bold" color={PRIMARY_BLACK}>
          {formatMoney(transactionDetail?.payment_amount)}
        </Text>
      )
    },
    {
      title: 'Promo',
      component: (
        <>
          {transactionDetail?.promos?.length === 0 ? (
            <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
              No aplica
            </Text>
          ) : (
            transactionDetail?.promos?.map((promo) => (
              <Text key={promo?.payment_id} variant="Callout-Regular" color={PRIMARY_BLACK}>
                {promo?.title}
              </Text>
            ))
          )}
        </>
      )
    },
    {
      title: 'Estado',
      component: (
        <>
          <StatusLabel
            backgroundColor={StatusTransactionLabelColor[`${transactionDetail?.payment_status}`]}
            label={getStatusTransactionLabel(transactionDetail?.payment_status)}
          />
          {hasP2mInfoPermission && (
            <div className={classes.divider}>
              <TransactionOperationError
                operationError={transactionDetail?.operation_error}
                originalErrorCode={transactionDetail?.original_error_code}
              />
            </div>
          )}
        </>
      )
    },
    {
      title: 'External Payment ID',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {'No se encontró External Payment ID'}
        </Text>
      )
    },
    {
      title: 'ID QR',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {'No se encontró ID de QR'}
        </Text>
      )
    },
    {
      title: 'ID de pago',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {transactionDetail?.payment_id ? transactionDetail?.payment_id : 'No se encontró ID de pago'}
        </Text>
      )
    },
    {
      title: 'Referencia',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {transactionDetail?.reference_code
            ? transactionDetail?.reference_code
            : 'No se encontró referencia'}
        </Text>
      )
    },
    {
      title: '',
      component: <Divider className={classes.divider} />
    },
    {
      title: 'Adquiriente',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {transactionDetail?.qr_acquirer_name
            ? transactionDetail?.qr_acquirer_name
            : 'No se encontró adquiriente'}
        </Text>
      )
    },
    {
      title: 'Marca de tarjeta',
      component: (
        <>
          {transactionDetail?.card_issuer === 'Dinero en Cuenta' ? (
            <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
              {transactionDetail?.card_issuer}
            </Text>
          ) : (
            <Icon
              source={getImageIssuer(transaction?.card?.issuer?.image_url)}
              size={IconSize.MEDIUM}
              isSquare={false}
            />
          )}
        </>
      )
    },
    {
      title: 'Tipo de tarjeta',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {getNameCardType(transactionDetail?.card_type)}
        </Text>
      )
    },
    {
      title: 'Número de tarjeta',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {transactionDetail?.last_digits
            ? `******${transactionDetail?.last_digits}`
            : 'No se encontró el dato'}
        </Text>
      )
    },
    {
      title: 'Cuotas',
      component: (
        <Text variant="Callout-Regular" color={PRIMARY_BLACK}>
          {transactionDetail?. installments_quantity
            ? `${transactionDetail?. installments_quantity}`
            : 'No se encontró el dato'}
        </Text>
      )
    },
    hasP2mInfoPermission && transactionDetail?.additional_info
      ? {
          title: '',
          component: <TransactionLogDetail logDetail={transactionDetail.additional_info} />
        }
      : {},
    {
      title: '',
      component: (
        <div>
          <div className={classes.footerInfo}>
            <div>
              <Icon source={warningIcon} size={IconSize.REGULAR} />
            </div>
            <div className={classes.footerTitle}>
              <Text variant="Caption-Medium" color={PRIMARY_BLACK}>
                <b>¿Tenés dudas sobre la transacción?</b>
              </Text>
              <div>
                <Text variant="Body-Regular" color={PRIMARY_BLACK}>
                  MODO no se encarga de la acreditación ni la devolución de las compras de tus clientes. En
                  estos casos te sugerimos que te contactes con tu adquirente.
                </Text>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];
};
